import {Pair} from "./UtilFunctions";
import {Equality} from "../../types/GenericTypes";

function DefaultEquality<T>(): Equality<T> {
    return (a, b) => a === b;
}

export class Optional<T> {

    public static EMPTY = new Optional();

    private constructor(private value: T | null | undefined = null) {
    }

    public map<R>(fn: (val: T) => R | null | undefined): Optional<R> {
        return this.value == null ? Optional.empty() : Optional.of(fn(this.value));
    }

    public flatMap<R>(fn: (val: T) => Optional<R>): Optional<R> {
        return this.value == null ? Optional.empty() : fn(this.value);
    }

    public get() {
        if (this.value != null) {
            return this.value;
        }
        throw Error("Cannot get from an empty Optional");
    }

    public orElse(alternative: T): T {
        return this.value == null ? alternative : this.value;
    }

    public orElseGet(fn: () => T): T {
        return this.value == null ? fn() : this.value;
    }

    public orNull(): T | null {
        return this.value == null ? null : this.value;
    }

    public orElseThrow(fn: () => Error): T {
        if (this.value == null) {
            throw fn();
        }
        return this.value;
    }

    public or(val: T | Optional<T> | null | undefined): Optional<T> {
        if (val instanceof Optional) {
            return this.value == null ? val : this;
        }

        return this.value == null ? Optional.of(val) : this;
    }

    public orGet(fn: () => T | null | undefined): Optional<T> {
        return this.value == null ? Optional.of(fn()) : this;
    }

    /* Double declaration because the compiler says so for some reason */
    public filter<S extends T>(fn: (val: T) => val is S): Optional<S>;
    public filter(fn: (val: T) => boolean): Optional<T>;

    public filter(fn: (val: T) => boolean): Optional<T> {
        return this.value == null ? Optional.empty() : Optional.of(fn(this.value) ? this.value : null);
    }

    public isPresent(): boolean {
        return this.value != null;
    }

    public ifPresent(consumer: (val: T) => void): Optional<T> {
        if (this.value != null) {
            consumer(this.value);
        }
        return this;
    }

    public doIfPresent(consumer: (val: T) => void): Optional<T> {
        if (this.value != null) {
            consumer(this.value);
        }
        return this;
    }

    public doIfNotPresent(fn: () => void): Optional<T> {
        if (this.value == null) {
            fn();
        }
        return this;
    }

    public isEmpty(): boolean {
        return this.value == null;
    }

    public ifEmpty(fn: () => void) {
        if (this.value == null) {
            fn();
        }
    }

    public static of<T>(val: T | null | undefined): Optional<T> {
        return val == null ? Optional.empty() : new Optional(val);
    }

    public static empty<T>(): Optional<T> {
        return Optional.EMPTY as Optional<T>;
    }

    public static zip<L, R>(o1: Optional<L>, o2: Optional<R>): Optional<[L, R]> {
        if (o1.isEmpty() || o2.isEmpty()) {
            return Optional.empty();
        } else {
            const left = o1.get();
            const right = o2.get();
            const pair = Pair(left, right);
            return Optional.of(pair);
        }
    }

    public equals(other: Optional<T>, equality: Equality<T> = DefaultEquality()): boolean {
        if (this.isEmpty() !== other.isEmpty()) {
            return false;
        } else if (this.isEmpty()) {
            return false;
        } else {
            return equality(this.value!, other.value!);
        }
    }
}
